import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare, Paperclip, Send, Trash2, Download, File,
  Clock, User, Calendar, Loader2, X, Plus, Activity, Timer,
  Repeat, RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useTaskStore, useUIStore, useAuthStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TaskDetailDialog = ({ task, open, onOpenChange, onSave }) => {
  const { fetchComments, addComment, deleteComment, fetchAttachments, uploadAttachment, deleteAttachment, fetchActivity, fetchTimeEntries, logTime, deleteTimeEntry, createNextOccurrence } = useTaskStore();
  const { language } = useUIStore();
  const { user: currentUser } = useAuthStore();
  const { t } = useTranslation(language);
  const fileInputRef = useRef(null);
  
  const [editingTask, setEditingTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Time tracking state
  const [timeEntries, setTimeEntries] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [isLoadingTime, setIsLoadingTime] = useState(false);
  const [newTimeHours, setNewTimeHours] = useState('');
  const [newTimeDesc, setNewTimeDesc] = useState('');
  const [isLoggingTime, setIsLoggingTime] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (task && open) {
      setEditingTask({ ...task });
      loadComments();
      loadAttachments();
      loadActivity();
      loadTimeEntries();
    }
  }, [task, open]);

  const loadComments = async () => {
    if (!task?.id) return;
    setIsLoadingComments(true);
    try {
      const data = await fetchComments(task.id);
      setComments(data || []);
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const loadAttachments = async () => {
    if (!task?.id) return;
    setIsLoadingAttachments(true);
    try {
      const data = await fetchAttachments(task.id);
      setAttachments(data || []);
    } catch (error) {
      console.error('Failed to load attachments:', error);
    } finally {
      setIsLoadingAttachments(false);
    }
  };

  const loadActivity = async () => {
    if (!task?.id) return;
    try {
      const data = await fetchActivity(task.id);
      setActivity(data || []);
    } catch (error) {
      console.error('Failed to load activity:', error);
    }
  };

  const loadTimeEntries = async () => {
    if (!task?.id) return;
    setIsLoadingTime(true);
    try {
      const data = await fetchTimeEntries(task.id);
      setTimeEntries(data.entries || []);
      setTotalHours(data.total_hours || 0);
    } catch (error) {
      console.error('Failed to load time entries:', error);
    } finally {
      setIsLoadingTime(false);
    }
  };

  const handleLogTime = async () => {
    const hours = parseFloat(newTimeHours);
    if (isNaN(hours) || hours <= 0 || !task?.id) {
      toast.error('Please enter valid hours');
      return;
    }
    setIsLoggingTime(true);
    try {
      const entry = await logTime(task.id, hours, newTimeDesc);
      setTimeEntries([entry, ...timeEntries]);
      setTotalHours(totalHours + hours);
      setNewTimeHours('');
      setNewTimeDesc('');
      toast.success(`${hours}h logged`);
    } catch (error) {
      toast.error(error.message || 'Failed to log time');
    } finally {
      setIsLoggingTime(false);
    }
  };

  const handleDeleteTimeEntry = async (entryId, hours) => {
    try {
      await deleteTimeEntry(entryId);
      setTimeEntries(timeEntries.filter(e => e.id !== entryId));
      setTotalHours(Math.max(0, totalHours - hours));
      toast.success('Time entry deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete time entry');
    }
  };

  const handleCreateNextOccurrence = async () => {
    if (!task?.id) return;
    try {
      await createNextOccurrence(task.id);
      toast.success('Next occurrence created');
    } catch (error) {
      toast.error(error.message || 'Failed to create next occurrence');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !task?.id) return;
    setIsSending(true);
    try {
      const comment = await addComment(task.id, newComment);
      setComments([comment, ...comments]);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      toast.error(error.message || 'Failed to add comment');
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete comment');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !task?.id) return;
    
    setIsUploading(true);
    try {
      const attachment = await uploadAttachment(task.id, file);
      setAttachments([...attachments, attachment]);
      toast.success('File uploaded');
    } catch (error) {
      toast.error(error.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await deleteAttachment(attachmentId);
      setAttachments(attachments.filter(a => a.id !== attachmentId));
      toast.success('Attachment deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete attachment');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case 'comment_added': return <MessageSquare className="h-3 w-3" />;
      case 'attachment_added': return <Paperclip className="h-3 w-3" />;
      default: return <Activity className="h-3 w-3" />;
    }
  };

  const handleSave = () => {
    if (editingTask && onSave) {
      onSave(editingTask);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/10 max-w-2xl max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderKanban className="h-5 w-5 text-primary" />
            {language === 'hi' ? 'टास्क विवरण' : 'Task Details'}
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="time" className="gap-1">
              <Timer className="h-3 w-3" />
              {totalHours > 0 && <span className="text-xs">({totalHours}h)</span>}
            </TabsTrigger>
            <TabsTrigger value="comments" className="gap-1">
              <MessageSquare className="h-3 w-3" />
              {comments.length > 0 && <span className="text-xs">({comments.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="attachments" className="gap-1">
              <Paperclip className="h-3 w-3" />
              {attachments.length > 0 && <span className="text-xs">({attachments.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="activity">
              <Activity className="h-3 w-3" />
            </TabsTrigger>
          </TabsList>
          
          {/* Details Tab */}
          <TabsContent value="details" className="mt-4">
            {editingTask && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('title')}</Label>
                  <Input
                    value={editingTask.title}
                    onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('description')}</Label>
                  <Textarea
                    value={editingTask.description || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('priority')}</Label>
                    <Select
                      value={editingTask.priority || 'medium'}
                      onValueChange={(v) => setEditingTask({ ...editingTask, priority: v })}
                    >
                      <SelectTrigger className="bg-secondary/50 border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t('low')}</SelectItem>
                        <SelectItem value="medium">{t('medium')}</SelectItem>
                        <SelectItem value="high">{t('high')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('due_date')}</Label>
                    <Input
                      type="date"
                      value={editingTask.due_date ? editingTask.due_date.split('T')[0] : ''}
                      onChange={(e) => setEditingTask({ ...editingTask, due_date: e.target.value })}
                      className="bg-secondary/50 border-white/10"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-[#ff3366] animate-pulse" />
                    <Label>{language === 'hi' ? 'बिज़नेस क्रिटिकल' : 'Business Critical'}</Label>
                  </div>
                  <Checkbox
                    checked={editingTask.is_business_critical}
                    onCheckedChange={(checked) => setEditingTask({ ...editingTask, is_business_critical: checked })}
                  />
                </div>
                
                {/* Recurring Task Options */}
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/30 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Repeat className="h-4 w-4 text-primary" />
                      <Label>{language === 'hi' ? 'दोहराया जाने वाला टास्क' : 'Recurring Task'}</Label>
                    </div>
                    <Checkbox
                      checked={editingTask.is_recurring || false}
                      onCheckedChange={(checked) => setEditingTask({ ...editingTask, is_recurring: checked })}
                    />
                  </div>
                  
                  {editingTask.is_recurring && (
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Pattern</Label>
                        <Select
                          value={editingTask.recurrence_pattern || 'daily'}
                          onValueChange={(v) => setEditingTask({ ...editingTask, recurrence_pattern: v })}
                        >
                          <SelectTrigger className="bg-secondary/50 border-white/10 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Every</Label>
                        <Input
                          type="number"
                          min="1"
                          value={editingTask.recurrence_interval || 1}
                          onChange={(e) => setEditingTask({ ...editingTask, recurrence_interval: parseInt(e.target.value) || 1 })}
                          className="bg-secondary/50 border-white/10 h-8"
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">End Date (optional)</Label>
                        <Input
                          type="date"
                          value={editingTask.recurrence_end_date || ''}
                          onChange={(e) => setEditingTask({ ...editingTask, recurrence_end_date: e.target.value })}
                          className="bg-secondary/50 border-white/10 h-8"
                        />
                      </div>
                    </div>
                  )}
                  
                  {editingTask.is_recurring && editingTask.status === 'done' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCreateNextOccurrence}
                      className="w-full gap-2"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Create Next Occurrence
                    </Button>
                  )}
                </div>
                
                {/* Estimated Hours */}
                <div className="space-y-2">
                  <Label>{language === 'hi' ? 'अनुमानित घंटे' : 'Estimated Hours'}</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editingTask.estimated_hours || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, estimated_hours: parseFloat(e.target.value) || null })}
                    placeholder="e.g., 4"
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
              </div>
            )}
          </TabsContent>
          
          {/* Time Tracking Tab */}
          <TabsContent value="time" className="mt-4">
            <div className="space-y-4">
              {/* Time Summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/30 text-center">
                  <p className="text-2xl font-bold text-primary">{totalHours.toFixed(1)}h</p>
                  <p className="text-xs text-muted-foreground">Logged</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/50 border border-white/10 text-center">
                  <p className="text-2xl font-bold">{editingTask?.estimated_hours || '-'}h</p>
                  <p className="text-xs text-muted-foreground">Estimated</p>
                </div>
              </div>
              
              {/* Log Time Form */}
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.25"
                  min="0.25"
                  placeholder="Hours"
                  value={newTimeHours}
                  onChange={(e) => setNewTimeHours(e.target.value)}
                  className="bg-secondary/50 border-white/10 w-24"
                />
                <Input
                  placeholder="What did you work on?"
                  value={newTimeDesc}
                  onChange={(e) => setNewTimeDesc(e.target.value)}
                  className="bg-secondary/50 border-white/10 flex-1"
                />
                <Button onClick={handleLogTime} disabled={isLoggingTime || !newTimeHours}>
                  {isLoggingTime ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
              
              {/* Time Entries List */}
              <ScrollArea className="h-[200px]">
                {isLoadingTime ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : timeEntries.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Timer className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No time logged yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {timeEntries.map((entry) => (
                      <motion.div
                        key={entry.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center">
                            <span className="text-sm font-bold text-primary">{entry.hours}h</span>
                          </div>
                          <div>
                            <p className="text-sm">{entry.description || 'No description'}</p>
                            <p className="text-xs text-muted-foreground">
                              {entry.logged_by?.name} • {entry.date}
                            </p>
                          </div>
                        </div>
                        {(entry.user_id === currentUser?.id || currentUser?.is_admin) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDeleteTimeEntry(entry.id, entry.hours)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>
          
          {/* Comments Tab */}
          <TabsContent value="comments" className="mt-4">
            <div className="space-y-4">
              {/* Add Comment */}
              <div className="flex gap-2">
                <Input
                  placeholder={language === 'hi' ? 'टिप्पणी जोड़ें...' : 'Add a comment...'}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  className="bg-secondary/50 border-white/10"
                />
                <Button onClick={handleAddComment} disabled={isSending || !newComment.trim()}>
                  {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              
              {/* Comments List */}
              <ScrollArea className="h-[250px]">
                {isLoadingComments ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{language === 'hi' ? 'कोई टिप्पणी नहीं' : 'No comments yet'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {comments.map((comment) => (
                      <motion.div
                        key={comment.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-3 rounded-lg bg-secondary/30 border border-white/5"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs bg-primary/20">
                                {comment.author?.name?.substring(0, 2).toUpperCase() || '??'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium">{comment.author?.name || 'Unknown'}</span>
                            <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                          </div>
                          {(comment.user_id === currentUser?.id || currentUser?.is_admin) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleDeleteComment(comment.id)}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          )}
                        </div>
                        <p className="mt-2 text-sm">{comment.content}</p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>
          
          {/* Attachments Tab */}
          <TabsContent value="attachments" className="mt-4">
            <div className="space-y-4">
              {/* Upload Button */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="gap-2"
                >
                  {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  {language === 'hi' ? 'फ़ाइल अपलोड करें' : 'Upload File'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <span className="text-xs text-muted-foreground">Max 10MB</span>
              </div>
              
              {/* Attachments List */}
              <ScrollArea className="h-[250px]">
                {isLoadingAttachments ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : attachments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{language === 'hi' ? 'कोई अटैचमेंट नहीं' : 'No attachments yet'}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {attachments.map((attachment) => (
                      <motion.div
                        key={attachment.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center">
                            <File className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium truncate max-w-[200px]">{attachment.filename}</p>
                            <p className="text-xs text-muted-foreground">{formatFileSize(attachment.size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => window.open(`${API_URL}/api/attachments/${attachment.id}/download`, '_blank')}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {(attachment.uploaded_by === currentUser?.id || currentUser?.is_admin) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDeleteAttachment(attachment.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>
          
          {/* Activity Tab */}
          <TabsContent value="activity" className="mt-4">
            <ScrollArea className="h-[300px]">
              {activity.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{language === 'hi' ? 'कोई गतिविधि नहीं' : 'No activity yet'}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activity.map((item) => (
                    <div key={item.id} className="flex items-start gap-3 p-2">
                      <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center mt-0.5">
                        {getActivityIcon(item.action)}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm">
                          <span className="font-medium">{item.actor?.name || 'Someone'}</span>
                          {' '}
                          {item.action === 'comment_added' && 'added a comment'}
                          {item.action === 'attachment_added' && `uploaded ${item.details?.filename || 'a file'}`}
                          {!['comment_added', 'attachment_added'].includes(item.action) && item.action.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
        
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSave}>
            {language === 'hi' ? 'सहेजें' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Missing import fix
const FolderKanban = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/>
    <path d="M8 10v4"/>
    <path d="M12 10v2"/>
    <path d="M16 10v6"/>
  </svg>
);

export default TaskDetailDialog;
